import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { courseOfferService, courseService } from '../../services/api';
import ExportCSVButton from '../Common/ExportCSVButton';
import { exportToCSV } from '../../utils/csvExport';

const CourseOfferList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courseOffers, setCourseOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nameFilter, setNameFilter] = useState(searchParams.get('name') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [courseFilter, setCourseFilter] = useState(searchParams.get('courseId') || '');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [archiveFilter, setArchiveFilter] = useState(searchParams.get('archiveFilter') || 'active');

  // Action loading states
  const [actionLoading, setActionLoading] = useState({});

  // Export state
  const [exporting, setExporting] = useState(false);

  // Sincronizza filtri nell'URL per ripristino al back
  useEffect(() => {
    const params = {};
    if (nameFilter) params.name = nameFilter;
    if (typeFilter) params.type = typeFilter;
    if (courseFilter) params.courseId = courseFilter;
    if (archiveFilter && archiveFilter !== 'active') params.archiveFilter = archiveFilter;
    if (page && page !== 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [page, nameFilter, typeFilter, courseFilter, archiveFilter, setSearchParams]);

  const loadCourseOffers = useCallback(async () => {
    try {
      setLoading(true);
      const isPayPerUse = null; // Gestiamo il filtro tramite offerType
      const offerType = typeFilter || null;
      const courseId = courseFilter || null;
      const result = await courseOfferService.getAll(page, 10, nameFilter || null, isPayPerUse, courseId, false, offerType, archiveFilter);
      
      if (result.success) {
        setCourseOffers(result.data.items);
        setTotalPages(result.data.totalPages);
        setTotalCount(result.data.totalCount);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel caricamento degli abbonamenti');
    } finally {
      setLoading(false);
    }
  }, [page, nameFilter, typeFilter, courseFilter, archiveFilter]);

  const loadCoursesAsync = useCallback((inputValue) => {
    return new Promise((resolve) => {
      courseService.getCoursesSummary(inputValue).then(response => {
        if (response.success) {
          const formattedCourses = (response.data || []).map(course => ({
            value: course.id,
            label: course.name
          }));
          resolve(formattedCourses);
        } else {
          resolve([]);
        }
      }).catch(() => {
        resolve([]);
      });
    });
  }, []);

  useEffect(() => {
    loadCourseOffers();
  }, [loadCourseOffers]);

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo abbonamento?')) {
      const result = await courseOfferService.delete(id);
      if (result.success) {
        loadCourseOffers();
      } else {
        setError(result.message);
      }
    }
  };

  const handleArchive = async (id, isArchived) => {
    const action = isArchived ? 'archiviare' : 'ripristinare';
    if (!window.confirm(`Sei sicuro di voler ${action} questo abbonamento?`)) {
      return;
    }

    const key = `archive-${id}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const result = await courseOfferService.archive(id, isArchived);
      if (result.success) {
        setSuccess(result.message);
        loadCourseOffers();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(`Errore nell'${isArchived ? 'archiviazione' : 'ripristino'} dell'abbonamento`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'name') {
      setNameFilter(value);
    } else if (field === 'type') {
      setTypeFilter(value);
    } else if (field === 'course') {
      setCourseFilter(value);
    }
    setPage(1); // Reset to first page when filtering
  };

  const handleArchiveFilterChange = (filter) => {
    setArchiveFilter(filter);
    setPage(1);
  };

  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
    setCourseFilter(selectedOption?.value || '');
    setPage(1);
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    setExporting(true);
    setError('');
    
    try {
      const isPayPerUse = null; // Gestiamo il filtro tramite offerType
      const offerType = typeFilter || null;
      const courseId = courseFilter || null;
      const result = await courseOfferService.getAll(1, 10, nameFilter || null, isPayPerUse, courseId, true, offerType);

      if (result.success && result.data.items) {
        const columns = [
          { label: 'Nome', accessor: (o) => o.name },
          { label: 'Prezzo', accessor: (o) => `€${o.amount.toFixed(2)}` },
          { label: 'Tipo', accessor: (o) => {
            if (o.totalTickets && !o.durationMonths) {
              return `${o.totalTickets} ingressi`;
            } else if (o.durationMonths && !o.totalTickets) {
              return `Mensile (${o.durationMonths} mes${o.durationMonths === 1 ? 'e' : 'i'})`;
            }
            return 'Data Libera';
          }},
          { label: 'Corsi', accessor: (o) => o.courseOfferCourses?.map(coc => coc.course.name).join(', ') || 'Nessun corso' }
        ];
        
        exportToCSV(result.data.items, 'abbonamenti', columns);
      } else {
        setError(result.message || 'Errore durante l\'esportazione');
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Errore durante l\'esportazione degli abbonamenti');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getOfferType = (offer) => {
    if (offer.totalTickets && !offer.durationMonths) {
      return `${offer.totalTickets} ingressi`;
    } else if (offer.durationMonths && !offer.totalTickets) {
      return `Mensile (${offer.durationMonths} mes${offer.durationMonths === 1 ? 'e' : 'i'})`;
    }
    return 'Data Libera';
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Gestione Abbonamenti</h2>
            <div>
              <ExportCSVButton 
                onExport={handleExportCSV}
                loading={exporting}
                disabled={loading}
                className="me-2"
              />
              <Link to="/course-offers/new" className="btn btn-primary">
                <i className="fas fa-plus me-2"></i>
                Nuovo Abbonamento
              </Link>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label htmlFor="nameFilter" className="form-label">Filtra per nome</label>
                  <input
                    type="text"
                    className="form-control"
                    id="nameFilter"
                    placeholder="Cerca per nome..."
                    value={nameFilter}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="typeFilter" className="form-label">Filtra per tipo</label>
                  <select
                    className="form-select"
                    id="typeFilter"
                    value={typeFilter}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    <option value="">Tutti i tipi</option>
                    <option value="payPerUse">Ingressi</option>
                    <option value="monthly">Mensile</option>
                    <option value="freeDate">Data Libera</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label htmlFor="courseFilter" className="form-label">Filtra per corso</label>
                  <AsyncSelect
                    id="courseFilter"
                    cacheOptions
                    defaultOptions
                    loadOptions={loadCoursesAsync}
                    onChange={handleCourseChange}
                    value={selectedCourse}
                    placeholder="Seleziona un corso..."
                    noOptionsMessage={() => "Nessun corso trovato"}
                    loadingMessage={() => "Caricamento corsi..."}
                    isClearable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '38px'
                      })
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Course Offers Table */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Abbonamenti</span>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${archiveFilter === 'active' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handleArchiveFilterChange('active')}
                >
                  Attivi
                </button>
                <button
                  type="button"
                  className={`btn ${archiveFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handleArchiveFilterChange('all')}
                >
                  Tutti
                </button>
                <button
                  type="button"
                  className={`btn ${archiveFilter === 'archived' ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => handleArchiveFilterChange('archived')}
                >
                  <i className="fas fa-archive me-1"></i>
                  Archiviati
                </button>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                  </div>
                </div>
              ) : courseOffers.length === 0 ? (
                <div className="text-center text-muted">
                  <p>Nessun abbonamento trovato</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Nome</th>
                          <th>Tipo</th>
                          <th>Corso</th>
                          <th>Prezzo</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courseOffers.map((offer) => (
                          <tr key={offer.id} className={offer.isArchived ? 'table-warning opacity-75' : ''}>
                            <td>
                              <strong>{offer.name}</strong>
                              {offer.isArchived && (
                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7em' }}>
                                  <i className="fas fa-archive me-1"></i>Archiviato
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${offer.totalTickets ? 'bg-info' : 'bg-success'}`}>
                                {getOfferType(offer)}
                              </span>
                            </td>
                            <td>
                              {offer.courseOfferCourses && offer.courseOfferCourses.length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {offer.courseOfferCourses.map((coc, index) => (
                                    <Link 
                                      key={index}
                                      to={`/courses?search=${encodeURIComponent(coc.course.name)}&academicYear=${coc.course.academicYear}`} 
                                      className="text-decoration-none"
                                    >
                                      <span className="badge bg-primary">
                                        {coc.course.name} ({coc.course.academicYear}/{coc.course.academicYear + 1})
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              ) : (
                                <span className="badge bg-secondary">
                                  Tutti i corsi
                                </span>
                              )}
                            </td>
                            <td>
                              <strong>{formatCurrency(offer.amount)}</strong>
                              {offer.hasInstallments && (
                                <div>
                                  <span
                                    className="badge bg-light text-dark border mt-1"
                                    title={`Totale a rate: ${formatCurrency(offer.installmentTotalAmount || offer.amount)}`}
                                  >
                                    <i className="fas fa-calendar-alt text-primary me-1"></i>
                                    A rate ({offer.installmentCount} rate{offer.installmentTotalAmount ? ` • ${formatCurrency(offer.installmentTotalAmount)}` : ''})
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="btn-group" role="group">
                                <Link
                                  to={`/course-offers/${offer.id}/edit`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Modifica"
                                >
                                  <i className="fas fa-edit"></i>
                                </Link>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${offer.isArchived ? 'btn-outline-success' : 'btn-outline-warning'}`}
                                  title={offer.isArchived ? 'Ripristina' : 'Archivia'}
                                  onClick={() => handleArchive(offer.id, !offer.isArchived)}
                                  disabled={actionLoading[`archive-${offer.id}`]}
                                >
                                  {actionLoading[`archive-${offer.id}`] ? (
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                  ) : (
                                    <i className={`fas fa-${offer.isArchived ? 'undo' : 'archive'}`}></i>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  title="Elimina"
                                  onClick={() => handleDelete(offer.id)}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label="Navigazione pagine">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                          >
                            Precedente
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(i + 1)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                          >
                            Successiva
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}

                  <div className="text-muted text-center mt-3">
                    Totale: {totalCount} abbonamenti
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseOfferList;